import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreateRoleInput, Role, UpdateRoleInput } from '@/application/permissions/permissionsTypes';
import { templateById } from '@/shared/permissions/roleTemplates';
import { PermissionTree } from './PermissionTree';
import { useMinWidthMd } from '@/presentation/hooks/useMinWidthMd';
import { RoleCompareHeader, RoleCompareTable } from './RoleCompareTable';
import { RoleMenuPreview } from './RoleMenuPreview';
import { Icon } from '@/presentation/components/common/Icon';
import { RoleTemplatePicker, type RoleTemplateSelection } from './RoleTemplatePicker';

export type RolesModalMode = 'create' | 'edit' | 'duplicate';

type TabId = 'permissions' | 'compare';

export function RolesModal({
  mode,
  role,
  rolesForCompare,
  onCreate,
  onUpdate,
  onSaved,
  onClose,
}: {
  mode: RolesModalMode;
  role: Role | null;
  rolesForCompare: Role[];
  onCreate: (input: CreateRoleInput) => Promise<void>;
  onUpdate: (id: string, input: UpdateRoleInput) => Promise<void>;
  onSaved: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const isDesktop = useMinWidthMd();
  const [tab, setTab] = useState<TabId>('permissions');
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [permissionKeys, setPermissionKeys] = useState<string[]>([]);
  const [compareRoleId, setCompareRoleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<RoleTemplateSelection>('custom');
  const [templatesOpen, setTemplatesOpen] = useState(false);

  useEffect(() => {
    setTab('permissions');
    setError(null);
    setCompareRoleId('');
    setSelectedTemplateId('custom');
    setTemplatesOpen(false);
    if (role) {
      setName(mode === 'duplicate' ? `${role.name} (copia)` : role.name);
      setIsActive(role.isActive);
      setPermissionKeys([...role.permissionKeys]);
    } else {
      setName('');
      setIsActive(true);
      setPermissionKeys([]);
    }
  }, [mode, role]);

  const applyTemplate = (id: RoleTemplateSelection) => {
    if (id === 'custom') {
      setSelectedTemplateId('custom');
      if (mode === 'create') {
        setPermissionKeys([]);
        setName((current) => {
          const previous = templateById(selectedTemplateId === 'custom' ? '' : selectedTemplateId);
          return previous && current.trim() === t(previous.savedNameKey) ? '' : current;
        });
      }
      return;
    }
    const template = templateById(id);
    if (!template) return;
    const previous = templateById(selectedTemplateId === 'custom' ? '' : selectedTemplateId);
    setSelectedTemplateId(id);
    setPermissionKeys([...template.permissionKeys]);
    setName((current) => {
      if (!current.trim() || (previous && current.trim() === t(previous.savedNameKey))) {
        return t(template.savedNameKey);
      }
      return current;
    });
    setTab('permissions');
  };

  const title =
    mode === 'create'
      ? t('permissions.modal.createTitle')
      : mode === 'duplicate'
        ? t('permissions.modal.duplicateTitle')
        : t('permissions.modal.editTitle');
  const compareRole = rolesForCompare.find((r) => r.id === compareRoleId);
  const thisRoleName = mode === 'edit' ? name.trim() || role?.name || title : title;

  const errorText = (code: string) => t(`permissions.errors.${code}`, { defaultValue: t('permissions.errors.generic') });

  const submit = async () => {
    if (!name.trim()) {
      setError(errorText('panel_role_name_required'));
      setTab('permissions');
      return;
    }
    if (permissionKeys.length === 0) {
      setError(errorText('panel_role_permissions_required'));
      setTab('permissions');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (mode === 'edit' && role) {
        await onUpdate(role.id, { name: name.trim(), permissionKeys, isActive });
      } else {
        await onCreate({ name: name.trim(), permissionKeys, isActive });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(errorText(err instanceof Error ? err.message : 'generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-sm sm:items-center sm:p-4">
      <div className={`flex max-h-[92vh] min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800 ${tab === 'permissions' ? 'max-h-[92vh] max-w-4xl max-sm:h-[92vh]' : 'max-w-2xl'}`}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h2 className="font-bold text-gray-900 dark:text-white">{title}</h2>
          {isDesktop && (
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              {t('common.close')}
            </button>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {tab === 'permissions' ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-hidden">
              {isDesktop && <RoleMenuPreview permissionKeys={permissionKeys} />}
              <div className="order-2 flex-1 space-y-4 p-5 md:order-1 md:min-h-0 md:overflow-y-auto">
                {!isDesktop && <RoleMenuPreview permissionKeys={permissionKeys} />}
                {error && (
                  <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">
                    {error}
                  </div>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="role-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('permissions.modal.name')}
                    </label>
                    <input
                      id="role-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                    />
                  </div>
                  <label className="flex items-end gap-2 pb-2 text-sm text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-teal-600" />
                    {t('permissions.modal.active')}
                  </label>
                </div>
                {mode === 'create' && (
                  <RoleTemplatePicker selectedId={selectedTemplateId} onSelect={applyTemplate} />
                )}
                {mode === 'edit' && (
                  <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      aria-expanded={templatesOpen}
                      onClick={() => setTemplatesOpen((open) => !open)}
                      className="flex min-h-11 w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-gray-700 dark:text-gray-200"
                    >
                      <Icon
                        path="M6 9l6 6 6-6"
                        className={`h-4 w-4 shrink-0 text-gray-400 transition-transform dark:text-gray-500 ${templatesOpen ? 'rotate-180' : ''}`}
                      />
                      <span>{t('permissions.templates.apply')}</span>
                    </button>
                    {templatesOpen && (
                      <div className="border-t border-gray-200 px-3 py-3 dark:border-gray-700">
                        <RoleTemplatePicker
                          selectedId={selectedTemplateId === 'custom' ? null : selectedTemplateId}
                          onSelect={applyTemplate}
                          showCustom={false}
                          hideHint
                        />
                      </div>
                    )}
                  </div>
                )}
                <Tabs tab={tab} setTab={setTab} />
                <PermissionTree value={permissionKeys} onChange={setPermissionKeys} />
              </div>
            </div>
          ) : (
            <>
              <div className="shrink-0 space-y-3 bg-white px-5 pt-5 dark:bg-gray-800">
                {error && (
                  <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200">
                    {error}
                  </div>
                )}
                {isDesktop && compareRole ? (
                  <div className="flex items-end justify-between gap-3">
                    <Tabs tab={tab} setTab={setTab} />
                    <div className="w-56">
                      <CompareRoleSelect
                        value={compareRoleId}
                        onChange={setCompareRoleId}
                        roles={rolesForCompare.filter((r) => r.id !== role?.id)}
                        hideLabel
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <Tabs tab={tab} setTab={setTab} />
                    {!isDesktop && (
                      <CompareRoleSelect
                        value={compareRoleId}
                        onChange={setCompareRoleId}
                        roles={rolesForCompare.filter((r) => r.id !== role?.id)}
                      />
                    )}
                  </>
                )}
                {compareRole && (
                  <RoleCompareHeader thisName={thisRoleName} otherName={compareRole.name} pinned />
                )}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-3">
                {compareRole ? (
                  <RoleCompareTable
                    thisName={thisRoleName}
                    otherName={compareRole.name}
                    thisKeys={permissionKeys}
                    otherKeys={compareRole.permissionKeys}
                    showHeader={false}
                  />
                ) : isDesktop ? (
                  <div className="flex flex-col items-center px-4 pt-10 text-center">
                    <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">{t('permissions.modal.compareEmpty')}</p>
                    <div className="mt-3 w-64">
                      <CompareRoleSelect
                        value={compareRoleId}
                        onChange={setCompareRoleId}
                        roles={rolesForCompare.filter((r) => r.id !== role?.id)}
                        hideLabel
                      />
                    </div>
                  </div>
                ) : (
                  <p className="pt-2 text-sm text-gray-500 dark:text-gray-400">{t('permissions.modal.compareEmpty')}</p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-5 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className={
              isDesktop
                ? 'min-h-11 rounded-lg px-4 text-sm text-gray-600 dark:text-gray-300'
                : 'min-h-11 rounded-xl border-2 border-gray-400 px-4 text-sm font-semibold text-gray-800 hover:bg-gray-50 dark:border-gray-500 dark:text-gray-100 dark:hover:bg-gray-700'
            }
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving}
            className="min-h-11 rounded-lg bg-teal-600 px-4 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
          >
            {saving ? t('permissions.modal.saving') : t('permissions.modal.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function CompareRoleSelect({
  value,
  onChange,
  roles,
  hideLabel = false,
}: {
  value: string;
  onChange: (id: string) => void;
  roles: Role[];
  hideLabel?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <label
        htmlFor="compare-role"
        className={
          hideLabel
            ? 'sr-only'
            : 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'
        }
      >
        {t('permissions.modal.compareWith')}
      </label>
      <select
        id="compare-role"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
      >
        <option value="">{t('permissions.modal.compareChoose')}</option>
        {roles.map((r) => (
          <option key={r.id} value={r.id}>{r.name}</option>
        ))}
      </select>
    </div>
  );
}

function Tabs({ tab, setTab }: { tab: TabId; setTab: (id: TabId) => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid w-full grid-cols-2 rounded-lg border border-gray-200 bg-gray-50 p-1 md:inline-flex md:w-auto dark:border-gray-700 dark:bg-gray-900/40">
      {(['permissions', 'compare'] as const).map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => setTab(id)}
          className={`min-h-10 rounded-md px-3 text-sm font-medium transition ${
            tab === id ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white' : 'text-gray-600 dark:text-gray-300'
          }`}
        >
          {t(`permissions.modal.tabs.${id}`)}
        </button>
      ))}
    </div>
  );
}
